import React from 'react';
import { useParams } from 'react-router';

import { DebriefAircraft, Vspeeds } from 'ff/debrief/models';
import {
    useCreateVspeedOverridesMutation,
    useGetAircraftListQuery,
    useGetRecentTailNumbersQuery,
    useGetTracklogByIdQuery,
    useGetVSpeedOverridesQuery,
    useLazyGetAircraftListQuery,
    useLazyGetVSpeedOverridesQuery,
    useSaveAircraftForTailNumberMutation,
    useUpdateTracklogOtherInfoMutation,
} from 'ff/debrief/features/TracklogApi';

export const useCurrentAircraft = () => {
    const params = useParams<{ tracklogId: string }>();
    const selectedTracklogId = params.tracklogId;
    const { data: tl } = useGetTracklogByIdQuery(selectedTracklogId, {
        skip: !selectedTracklogId,
    });

    const { data: recentTails } = useGetRecentTailNumbersQuery('');

    const { currentData: aircraftList } = useGetAircraftListQuery(
        tl?.tailNumber
    );

    const [triggerGetAircraftVspeeds] = useLazyGetVSpeedOverridesQuery();
    const [triggerGetAircraftList] = useLazyGetAircraftListQuery();

    const getVspeedsForTail = (
        tailNumber: string,
        manufacturer?: string,
        model?: string
    ) =>
        triggerGetAircraftVspeeds({
            tailNumber,
            manufacturer,
            model,
        }).unwrap();

    const getAircraftListForTail = (tailNumber: string) =>
        triggerGetAircraftList(tailNumber, true).unwrap();

    const { currentData: vSpeeds, ...vSpeedsQuery } =
        useGetVSpeedOverridesQuery(
            { tailNumber: tl?.tailNumber },
            {
                skip: !tl?.tailNumber || tl?.tailNumber === 'Unknown',
            }
        );

    const [createOverrides, createStatus] = useCreateVspeedOverridesMutation();
    const [saveAircraftModel] = useSaveAircraftForTailNumberMutation();
    const [updateTracklogOtherInfo] = useUpdateTracklogOtherInfoMutation();

    const hasVspeeds = React.useMemo(() => {
        if (
            vSpeedsQuery.isUninitialized ||
            vSpeedsQuery.isLoading ||
            tl?.analysisSummary?.status !== 'COMPLETED'
        ) {
            return null;
        }
        return Boolean(vSpeedsQuery.isSuccess && vSpeeds?.defaultVSpeed);
    }, [
        vSpeedsQuery.isUninitialized,
        vSpeedsQuery.isSuccess,
        Boolean(vSpeeds?.defaultVSpeed),
    ]);

    const setOverrides = (
        newOverrides: Vspeeds,
        preferredSpeedUnit: string,
        currentTail?: string
    ) => {
        return createOverrides({
            tailNumber: currentTail || tl.tailNumber,
            tracklogId: tl?.trackUuid,
            overrides: newOverrides,
            preferredSpeedUnit: preferredSpeedUnit,
        }).unwrap();
    };

    const updateTailNumber = (tail: string) => {
        return updateTracklogOtherInfo({
            objectId: tl?.metadata.objectId,
            tracklogId: tl?.trackUuid,
            updates: { tailNumber: tail },
        });
    };

    const setAircraftModel = (
        newAircraftModel: DebriefAircraft,
        currentTail?: string
    ) => {
        return saveAircraftModel({
            tailNumber: currentTail || tl?.tailNumber,
            ...newAircraftModel,
        }).unwrap();
    };

    const currentAircraft = vSpeeds
        ? {
              label: `${vSpeeds?.manufacturer} / ${vSpeeds?.model}`,
              manufacturer: vSpeeds?.manufacturer,
              model: vSpeeds?.model,
          }
        : null;

    const currentTailNumber =
        vSpeeds && tl?.tailNumber
            ? {
                  label: tl.tailNumber,
                  tailNumber: tl.tailNumber,
                  manufacturer: vSpeeds?.manufacturer,
                  model: vSpeeds?.model,
              }
            : null;

    const currentSpeedUnit =
        vSpeeds?.tailNumber === tl?.tailNumber
            ? vSpeeds?.preferredSpeedUnit
            : undefined;

    return {
        currentTailNumber: currentTailNumber,
        currentAircraft: currentAircraft,
        hasVspeeds: hasVspeeds,
        recentTails: recentTails,
        aircraftList: aircraftList,
        standardSpeeds: vSpeeds?.defaultVSpeed,
        vSpeedOverrides: vSpeeds?.aircraftVSpeed,
        setOverrides: setOverrides,
        updateTailNumber: updateTailNumber,
        setAircraftModel: setAircraftModel,
        createStatus: createStatus,
        getVspeedsForTail: getVspeedsForTail,
        getAircraftListForTail: getAircraftListForTail,
        speedUnit: currentSpeedUnit,
        isLoadingVspeeds: vSpeedsQuery.isLoading || vSpeedsQuery.isFetching,
    };
};
